/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Modal, Tag, Spin, Tooltip } from 'antd';
import {
    BellOutlined, ReloadOutlined,
    CarOutlined, ToolOutlined, WarningOutlined,
    UserOutlined, GlobalOutlined, FileTextOutlined,
    SafetyCertificateOutlined, DashboardOutlined,
    ExclamationCircleOutlined, ClockCircleOutlined,
    FireOutlined, ThunderboltOutlined, RiseOutlined,
    StarOutlined
} from '@ant-design/icons';
import moment from 'moment';
import FleetService from '../../services/FleetService';
import { fleetPerms } from '../../utils/fleetPermissions';

const getUserRole = () => fleetPerms();
const toN  = (v) => Number(v || 0);
const fmtD = (v) => (!v || v === '0000-00-00') ? '—' : moment(v).format('DD MMM YYYY');

const PRIORITY = {
    critical: { label:'Critical', bg:'#fff1f0', border:'#ffa39e', text:'#cf1322', dot:'#f5222d', order:1 },
    high:     { label:'High',     bg:'#fff7e6', border:'#ffd591', text:'#d46b08', dot:'#fa8c16', order:2 },
    medium:   { label:'Medium',   bg:'#e6f4ff', border:'#91caff', text:'#0958d9', dot:'#1677ff', order:3 },
    info:     { label:'Info',     bg:'#f6ffed', border:'#b7eb8f', text:'#389e0d', dot:'#52c41a', order:4 },
    low:      { label:'Info',     bg:'#f6ffed', border:'#b7eb8f', text:'#389e0d', dot:'#52c41a', order:5 },
};

function AlertCard({ alert, onView }) {
    const p = PRIORITY[alert.priority] || PRIORITY.medium;
    return (
        <div style={{
            background: p.bg, border: `1px solid ${p.border}`,
            borderRadius: 8, padding: '12px 14px', marginBottom: 8,
            borderLeft: `3px solid ${p.dot}`,
            display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
            <div style={{ fontSize: 18, color: p.dot, flexShrink: 0, marginTop: 1 }}>{alert.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                        <span style={{ fontWeight: 600, fontSize: 13, color: p.text }}>{alert.title}</span>
                        <Tag style={{ marginLeft: 8, fontSize: 10, background: p.bg, borderColor: p.border, color: p.text }}>
                            {alert.count} {alert.count === 1 ? 'item' : 'items'}
                        </Tag>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                        {onView && (
                            <Tooltip title="View details">
                                <Button size="small" type="link"
                                    style={{ padding: '0 4px', fontSize: 11, color: p.text }}
                                    onClick={() => onView(alert)}>
                                    View all →
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 3, lineHeight: 1.45 }}>{alert.description}</div>
                {alert.items && alert.items.length > 0 && (
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
                        {alert.items.slice(0, 8).map((item, i) => (
                            <span key={i} style={{
                                fontSize: 11, padding: '1px 8px', borderRadius: 10,
                                background: '#fff', border: `1px solid ${p.border}`,
                                color: p.text, fontWeight: 500,
                            }}>{item}</span>
                        ))}
                        {alert.items.length > 8 && (
                            <span style={{ fontSize: 11, color: '#aaa', alignSelf: 'center' }}>
                                +{alert.items.length - 8} more
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function AlertDetailModal({ alert, onClose }) {
    if (!alert) return null;
    const p = PRIORITY[alert.priority] || PRIORITY.medium;
    return (
        <Modal open={!!alert} onCancel={onClose} width={680} destroyOnClose
            footer={<Button onClick={onClose}>Close</Button>}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: p.dot }}>{alert.icon}</span>
                    <span>{alert.title}</span>
                    <Tag style={{ fontSize: 10, background: p.bg, borderColor: p.border, color: p.text }}>
                        {alert.count} {alert.count === 1 ? 'item' : 'items'}
                    </Tag>
                </div>
            }>
            {alert.detail ? alert.detail() : (
                <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: '#1a3a5c', color: '#fff' }}>
                                {alert.columns?.map(col => (
                                    <th key={col} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600 }}>{col}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {alert.rows?.map((row, i) => (
                                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9', borderBottom: '1px solid #f0f0f0' }}>
                                    {row.map((cell, j) => (
                                        <td key={j} style={{ padding: '8px 12px' }}>
                                            {typeof cell === 'object' ? cell : <span style={{ fontSize: 12 }}>{cell}</span>}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
}

export default function FleetNoticeBoard() {
    const [vehicles,    setVehicles]    = useState([]);
    const [drivers,     setDrivers]     = useState([]);
    const [incidents,   setIncidents]   = useState([]);
    const [costs,       setCosts]       = useState([]);
    const [docs,        setDocs]        = useState([]);
    const [deps,        setDeps]        = useState([]);
    const [fuelData,    setFuelData]    = useState([]);
    const [fuelBalance, setFuelBalance] = useState([]);   // ← NEW: correct running-balance model
    const [tcoData,     setTcoData]     = useState([]);
    const [noTripData,  setNoTripData]  = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [detailAlert, setDetailAlert] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [vRes, dRes, iRes, cRes, docRes, depRes, fRes, tcoRes, ntRes, fbRes] = await Promise.allSettled([
                FleetService.getVehiclesList(),
                FleetService.getDriversList(),
                FleetService.getIncidents(),
                FleetService.getCosts(''),
                FleetService.reportDocs(),
                FleetService.getDeployments(),
                FleetService.reportFuel(),
                FleetService.reportTCO(),
                FleetService.getVehiclesWithNoTrips(),
                FleetService.getFuelBalanceSummary
                    ? FleetService.getFuelBalanceSummary()
                    : Promise.resolve({ data: { code: 200, result: [] } }),
            ]);
            if (vRes.status   === 'fulfilled' && Number(vRes.value?.data?.code)   === 200) setVehicles(vRes.value.data.result   || []);
            if (dRes.status   === 'fulfilled' && Number(dRes.value?.data?.code)   === 200) setDrivers(dRes.value.data.result    || []);
            if (iRes.status   === 'fulfilled' && Number(iRes.value?.data?.code)   === 200) setIncidents(iRes.value.data.result  || []);
            if (cRes.status   === 'fulfilled' && Number(cRes.value?.data?.code)   === 200) setCosts(cRes.value.data.result      || []);
            if (docRes.status === 'fulfilled' && Number(docRes.value?.data?.code) === 200) setDocs(docRes.value.data.result     || []);
            if (depRes.status === 'fulfilled' && Number(depRes.value?.data?.code) === 200) setDeps(depRes.value.data.result?.deployments || []);
            if (fRes.status   === 'fulfilled' && Number(fRes.value?.data?.code)   === 200) setFuelData(fRes.value.data.result   || []);
            if (tcoRes.status === 'fulfilled' && Number(tcoRes.value?.data?.code) === 200) setTcoData(tcoRes.value.data.result  || []);
            if (ntRes.status  === 'fulfilled' && Number(ntRes.value?.data?.code)  === 200) setNoTripData(ntRes.value.data.result || []);
            if (fbRes.status  === 'fulfilled' && Number(fbRes.value?.data?.code)  === 200) setFuelBalance(fbRes.value.data.result || []);
        } catch(e) { console.error('Notice board load error', e); }
        setLoading(false);
        setLastRefresh(new Date());
    }, []);

    useEffect(() => { load(); }, []);

    const alerts = useMemo(() => {
        const all = [];

        // ── 1. Expired documents ──────────────────────────────────────────────
        const expiredDocs  = docs.filter(d => d.status === 'Expired');
        const expiringDocs = docs.filter(d => d.status === 'Expiring Soon' || (toN(d.days_remaining) >= 0 && toN(d.days_remaining) <= 30));
        if (expiredDocs.length > 0) all.push({
            key: 'docs_expired', priority: 'critical',
            icon: <SafetyCertificateOutlined />,
            title: 'Documents expired',
            count: expiredDocs.length,
            description: 'These vehicles have expired road disc, insurance, or fitness certificates. They are operating illegally.',
            items: [...new Set(expiredDocs.map(d => d.registration_no).filter(Boolean))],
            columns: ['Reg. No.', 'Doc Type', 'Expiry Date', 'Days Expired', 'Facility'],
            rows: expiredDocs.slice(0, 30).map(d => [
                <strong style={{ color: '#cf1322' }}>{d.registration_no || '—'}</strong>,
                d.doc_type, fmtD(d.expiry_date),
                <Tag color="error">{Math.abs(toN(d.days_remaining))} days ago</Tag>,
                d.facility,
            ]),
        });
        if (expiringDocs.length > 0) all.push({
            key: 'docs_expiring', priority: 'high',
            icon: <ClockCircleOutlined />,
            title: 'Documents expiring within 30 days',
            count: expiringDocs.length,
            description: 'Road disc, insurance, or fitness certificates due for renewal. Act before expiry to avoid non-compliance.',
            items: [...new Set(expiringDocs.map(d => d.registration_no).filter(Boolean))],
            columns: ['Reg. No.', 'Doc Type', 'Expiry Date', 'Days Left', 'Facility'],
            rows: expiringDocs.sort((a,b) => toN(a.days_remaining)-toN(b.days_remaining)).slice(0,30).map(d => [
                <strong style={{ color: '#d46b08' }}>{d.registration_no || '—'}</strong>,
                d.doc_type, fmtD(d.expiry_date),
                <Tag color="warning">{toN(d.days_remaining)} days</Tag>,
                d.facility,
            ]),
        });

        // ── 2. Driver licences ────────────────────────────────────────────────
        const licExpired  = drivers.filter(d => toN(d.licence_days_remaining) < 0);
        const licExpiring = drivers.filter(d => toN(d.licence_days_remaining) >= 0 && toN(d.licence_days_remaining) < 30);
        if (licExpired.length > 0) all.push({
            key: 'licence_expired', priority: 'critical',
            icon: <UserOutlined />,
            title: 'Driver licences EXPIRED — still assigned',
            count: licExpired.length,
            description: 'These drivers have expired licences but remain assigned to vehicles. This is a legal and liability risk.',
            items: licExpired.map(d => d.name),
            columns: ['Driver', 'Licence No.', 'Expired', 'Assigned Vehicle', 'Facility'],
            rows: licExpired.map(d => [
                <strong style={{ color: '#cf1322' }}>{d.name}</strong>,
                d.licence_no || '—',
                <Tag color="error">{Math.abs(toN(d.licence_days_remaining))}d ago</Tag>,
                d.current_vehicle_reg || <span style={{ color: '#aaa' }}>Unassigned</span>,
                d.facility || '—',
            ]),
        });
        if (licExpiring.length > 0) all.push({
            key: 'licence_expiring', priority: 'high',
            icon: <UserOutlined />,
            title: 'Driver licences expiring within 30 days',
            count: licExpiring.length,
            description: 'Notify these drivers and arrange renewals before the licence expires.',
            items: licExpiring.map(d => `${d.name} (${toN(d.licence_days_remaining)}d)`),
            columns: ['Driver', 'Licence Expiry', 'Days Left', 'Vehicle', 'Facility'],
            rows: licExpiring.sort((a,b) => toN(a.licence_days_remaining)-toN(b.licence_days_remaining)).map(d => [
                d.name, fmtD(d.licence_expiry),
                <Tag color="warning">{toN(d.licence_days_remaining)} days</Tag>,
                d.current_vehicle_reg || '—',
                d.facility || '—',
            ]),
        });

        // ── 3. Service overdue ────────────────────────────────────────────────
        const svcOverdue = vehicles.filter(v => v.service_due_flag === 'overdue');
        const svcDueSoon = vehicles.filter(v => v.service_due_flag === 'due_soon');
        if (svcOverdue.length > 0) all.push({
            key: 'service_overdue', priority: 'critical',
            icon: <ToolOutlined />,
            title: 'Service overdue',
            count: svcOverdue.length,
            description: 'These vehicles have passed their next scheduled service mileage. Schedule immediately.',
            items: svcOverdue.map(v => v.registration_no),
            columns: ['Reg. No.', 'Current Odometer', 'Next Service Due', 'Overdue By', 'Facility'],
            rows: svcOverdue.map(v => {
                const over = v.next_service_km && v.current_odometer ? toN(v.current_odometer) - toN(v.next_service_km) : null;
                return [
                    <strong style={{ color: '#cf1322' }}>{v.registration_no}</strong>,
                    v.current_odometer ? `${toN(v.current_odometer).toLocaleString()} km` : '—',
                    v.next_service_km  ? `${toN(v.next_service_km).toLocaleString()} km`  : '—',
                    over !== null ? <Tag color="error">{over.toLocaleString()} km over</Tag> : '—',
                    v.facility,
                ];
            }),
        });
        if (svcDueSoon.length > 0) all.push({
            key: 'service_due_soon', priority: 'high',
            icon: <ToolOutlined />,
            title: 'Service due within 500 km',
            count: svcDueSoon.length,
            description: 'These vehicles are approaching their next service milestone. Book in advance.',
            items: svcDueSoon.map(v => v.registration_no),
            columns: ['Reg. No.', 'Current Odometer', 'Next Service Due', 'KM Remaining', 'Facility'],
            rows: svcDueSoon.map(v => {
                const left = v.next_service_km && v.current_odometer ? toN(v.next_service_km) - toN(v.current_odometer) : null;
                return [
                    v.registration_no,
                    v.current_odometer ? `${toN(v.current_odometer).toLocaleString()} km` : '—',
                    v.next_service_km  ? `${toN(v.next_service_km).toLocaleString()} km`  : '—',
                    left !== null ? <Tag color="warning">{left.toLocaleString()} km left</Tag> : '—',
                    v.facility,
                ];
            }),
        });

        // ── 4. Unverified vehicles ────────────────────────────────────────────
        const unverified = vehicles.filter(v =>
            !v.verification_status || v.verification_status === 'pending' || v.verification_status === 'unverified'
        );
        if (unverified.length > 0) all.push({
            key: 'unverified', priority: 'high',
            icon: <ExclamationCircleOutlined />,
            title: 'Unverified vehicles',
            count: unverified.length,
            description: 'These vehicles have not been physically verified. Asset data may be inaccurate or vehicles may no longer exist.',
            items: unverified.slice(0, 15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Facility', 'Region', 'Verification Status'],
            rows: unverified.slice(0, 30).map(v => [
                v.registration_no, v.vehicle_type, v.facility, v.region || '—',
                <Tag color="default">{v.verification_status || 'Not verified'}</Tag>,
            ]),
        });

        // ── 5. High mileage ───────────────────────────────────────────────────
        const highMileage = vehicles.filter(v => toN(v.current_odometer) > 200000);
        if (highMileage.length > 0) all.push({
            key: 'high_mileage', priority: 'high',
            icon: <RiseOutlined />,
            title: 'Vehicles over 200,000 km',
            count: highMileage.length,
            description: 'These vehicles have exceeded 200,000 km. Consider end-of-life assessment or disposal planning.',
            items: highMileage.map(v => `${v.registration_no} (${toN(v.current_odometer).toLocaleString()} km)`),
            columns: ['Reg. No.', 'Type', 'Odometer', 'Facility', 'Functional Status'],
            rows: highMileage.sort((a,b) => toN(b.current_odometer)-toN(a.current_odometer)).map(v => [
                <strong>{v.registration_no}</strong>,
                v.vehicle_type,
                <span style={{ fontWeight: 600, color: '#d46b08' }}>{toN(v.current_odometer).toLocaleString()} km</span>,
                v.facility,
                <Tag color={v.functional_status === 'Serviceable' ? 'green' : 'orange'}>{v.functional_status || '—'}</Tag>,
            ]),
        });

        // ── 6. No assigned driver ─────────────────────────────────────────────
        const noDriver = vehicles.filter(v => !v.current_driver_name);
        if (noDriver.length > 0) all.push({
            key: 'no_driver', priority: 'medium',
            icon: <UserOutlined />,
            title: 'Vehicles with no assigned driver',
            count: noDriver.length,
            description: 'These operational vehicles have no Primary driver assigned. No one is accountable for trip logs and incidents.',
            items: noDriver.slice(0, 15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Programme', 'Region', 'Facility'],
            rows: noDriver.slice(0, 30).map(v => [
                v.registration_no, v.vehicle_type,
                v.assigned_programme || <span style={{ color: '#aaa' }}>No programme</span>,
                v.region || '—', v.facility,
            ]),
        });

        // ── 7. No programme ───────────────────────────────────────────────────
        const noProgramme = vehicles.filter(v => !v.assigned_programme || v.assigned_programme.trim() === '');
        if (noProgramme.length > 0) all.push({
            key: 'no_programme', priority: 'medium',
            icon: <FileTextOutlined />,
            title: 'Vehicles not assigned to a programme',
            count: noProgramme.length,
            description: 'Vehicles without a programme assignment cannot be properly tracked for programmatic reporting and cost allocation.',
            items: noProgramme.slice(0, 15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Driver', 'Region', 'Facility'],
            rows: noProgramme.slice(0, 30).map(v => [
                v.registration_no, v.vehicle_type,
                v.current_driver_name || <span style={{ color: '#aaa' }}>Unassigned</span>,
                v.region || '—', v.facility,
            ]),
        });

        // ── 8. Poor condition ─────────────────────────────────────────────────
        const poorCondition = vehicles.filter(v => v.condition_rating && toN(v.condition_rating) > 0 && toN(v.condition_rating) < 3);
        if (poorCondition.length > 0) all.push({
            key: 'poor_condition', priority: 'critical',
            icon: <WarningOutlined />,
            title: 'Poor condition — rating below 3',
            count: poorCondition.length,
            description: 'These vehicles have a condition rating below 3/5. They may be unsafe to operate. Review for repair or disposal.',
            items: poorCondition.map(v => `${v.registration_no} (${v.condition_rating}/5)`),
            columns: ['Reg. No.', 'Type', 'Rating', 'Functional Status', 'Facility'],
            rows: poorCondition.sort((a,b) => toN(a.condition_rating)-toN(b.condition_rating)).map(v => [
                <strong style={{ color: '#cf1322' }}>{v.registration_no}</strong>,
                v.vehicle_type,
                <Tag color="error">{'★'.repeat(toN(v.condition_rating))}{'☆'.repeat(5-toN(v.condition_rating))} {v.condition_rating}/5</Tag>,
                v.functional_status || '—', v.facility,
            ]),
        });

        // ── 9. FUEL: Negative balance — correct running-balance model ──────────
        // Negative = consumed more than tank had → theft or unrecorded refuel
        const negBalance   = fuelBalance.filter(v => toN(v.current_balance) < 0);
        const lowBalance   = fuelBalance.filter(v => toN(v.current_balance) >= 0 && toN(v.current_balance) < 10);
        const unaccountedL = fuelBalance.reduce((s,v) => s + Math.max(0, -toN(v.current_balance)), 0);

        if (negBalance.length > 0) all.push({
            key: 'fuel_negative_balance', priority: 'critical',
            icon: <FireOutlined />,
            title: 'Fuel balance impossible — investigate for theft or unrecorded refuel',
            count: negBalance.length,
            description: `${unaccountedL.toFixed(1)}L unaccounted. Tank went negative — vehicle consumed more than it had. Either a refuel was not recorded, or fuel was siphoned.`,
            items: negBalance.map(v => `${v.reg} (${toN(v.current_balance).toFixed(1)}L)`),
            columns: ['Reg. No.', 'Type', 'Baseline', 'KM Driven', 'Topped Up', 'Est. Balance', 'Unaccounted'],
            rows: negBalance.sort((a,b) => toN(a.current_balance)-toN(b.current_balance)).map(v => [
                <strong style={{ color:'#cf1322' }}>{v.reg}</strong>,
                v.vehicleType || '—',
                <Tag color="blue" style={{ fontSize:10 }}>{v.baseline} L/100km</Tag>,
                `${toN(v.total_km).toLocaleString()} km`,
                <span style={{ color:'#1890ff' }}>{toN(v.total_fuelled).toFixed(1)} L</span>,
                <strong style={{ color:'#f5222d' }}>{toN(v.current_balance).toFixed(1)} L</strong>,
                <Tag color="error">{Math.abs(toN(v.current_balance)).toFixed(1)} L missing</Tag>,
            ]),
        });

        if (lowBalance.length > 0) all.push({
            key: 'fuel_low_balance', priority: 'high',
            icon: <FireOutlined />,
            title: 'Estimated tank below 10L — top-up needed',
            count: lowBalance.length,
            description: 'Based on km driven × baseline consumption rate, these vehicles have less than 10L estimated remaining. Prompt drivers to record a top-up before next trip.',
            items: lowBalance.map(v => `${v.reg} (~${toN(v.current_balance).toFixed(1)}L left)`),
            columns: ['Reg. No.', 'Type', 'Baseline Rate', 'KM Driven', 'Est. Balance', 'Action'],
            rows: lowBalance.sort((a,b) => toN(a.current_balance)-toN(b.current_balance)).map(v => [
                <strong style={{ color:'#d46b08' }}>{v.reg}</strong>,
                v.vehicleType || '—',
                <Tag color="blue" style={{ fontSize:10 }}>{v.baseline} L/100km</Tag>,
                `${toN(v.total_km).toLocaleString()} km`,
                <strong style={{ color:'#fa8c16' }}>{toN(v.current_balance).toFixed(1)} L</strong>,
                <Tag color="warning" style={{ fontSize:10 }}>Log top-up</Tag>,
            ]),
        });

        // ── 10. High fuel consumption (legacy L/100km signal) ─────────────────
        const highFuel = fuelData.filter(f => toN(f.litres_per_100km) > 20)
            .reduce((m, f) => {
                const key = f.registration_no;
                if (!m[key] || toN(f.litres_per_100km) > toN(m[key].litres_per_100km)) m[key] = f;
                return m;
            }, {});
        const highFuelList = Object.values(highFuel).sort((a,b) => toN(b.litres_per_100km)-toN(a.litres_per_100km));
        if (highFuelList.length > 0) all.push({
            key: 'high_fuel', priority: 'high',
            icon: <FireOutlined />,
            title: 'High fuel consumption — over 20 L/100km',
            count: highFuelList.length,
            description: 'These vehicles are consuming fuel at an unusually high rate. Check for mechanical issues, overloading, or driver behaviour.',
            items: highFuelList.slice(0, 10).map(f => `${f.registration_no} (${toN(f.litres_per_100km).toFixed(1)} L/100km)`),
            columns: ['Reg. No.', 'Type', 'L/100km', 'Total Litres', 'Facility'],
            rows: highFuelList.slice(0, 20).map(f => [
                f.registration_no, f.vehicle_type || '—',
                <Tag color="error">{toN(f.litres_per_100km).toFixed(1)} L/100km</Tag>,
                `${toN(f.total_litres).toFixed(0)} L`,
                f.facility || '—',
            ]),
        });

        // ── 11. Repair costs exceed procurement ───────────────────────────────
        const overServiced = tcoData.filter(v =>
            toN(v.repair_cost) > 0 && toN(v.procurement_cost) > 0 &&
            toN(v.repair_cost) > toN(v.procurement_cost)
        );
        if (overServiced.length > 0) all.push({
            key: 'over_serviced', priority: 'high',
            icon: <ThunderboltOutlined />,
            title: 'Repair costs exceed procurement cost',
            count: overServiced.length,
            description: 'These vehicles have cost more in repairs than they were worth at purchase. Strong candidates for disposal.',
            items: overServiced.map(v => `${v.registration_no} (E${toN(v.repair_cost).toLocaleString()})`),
            columns: ['Reg. No.', 'Type', 'Procurement Cost', 'Repair Cost', 'Ratio', 'Facility'],
            rows: overServiced.sort((a,b) => toN(b.repair_cost)-toN(a.repair_cost)).slice(0, 20).map(v => {
                const ratio = toN(v.procurement_cost) > 0 ? (toN(v.repair_cost) / toN(v.procurement_cost) * 100).toFixed(0) : 0;
                return [
                    <strong>{v.registration_no}</strong>, v.vehicle_type,
                    `E ${toN(v.procurement_cost).toLocaleString()}`,
                    <span style={{ fontWeight: 600, color: '#cf1322' }}>E {toN(v.repair_cost).toLocaleString()}</span>,
                    <Tag color="error">{ratio}% of purchase</Tag>,
                    v.facility,
                ];
            }),
        });

        // ── 12. Newly registered vehicles (last 90 days) ──────────────────────
        const cutoff = moment().subtract(90, 'days');
        const newVehicles = vehicles.filter(v => {
            if (!v.procurement_date || v.procurement_date === '0000-00-00') return false;
            return moment(v.procurement_date).isAfter(cutoff);
        });
        if (newVehicles.length > 0) all.push({
            key: 'new_vehicles', priority: 'info',
            icon: <CarOutlined />,
            title: 'Newly registered vehicles — last 90 days',
            count: newVehicles.length,
            description: 'These vehicles were recently added to the fleet. Ensure they have drivers assigned, documents uploaded, and programmes set.',
            items: newVehicles.map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Procurement Date', 'Driver', 'Programme', 'Facility'],
            rows: newVehicles.sort((a,b) => moment(b.procurement_date).diff(moment(a.procurement_date))).map(v => [
                <strong style={{ color: '#389e0d' }}>{v.registration_no}</strong>,
                v.vehicle_type, fmtD(v.procurement_date),
                v.current_driver_name || <Tag color="warning">Unassigned</Tag>,
                v.assigned_programme  || <Tag color="warning">No programme</Tag>,
                v.facility,
            ]),
        });

        // ── 13. Open high-severity incidents ──────────────────────────────────
        const openHighSev = incidents.filter(i =>
            i.status === 'Open' && ['High','Critical'].includes(i.severity) && !i.action_taken
        );
        if (openHighSev.length > 0) all.push({
            key: 'open_incidents', priority: 'critical',
            icon: <ExclamationCircleOutlined />,
            title: 'High/Critical incidents — no action taken',
            count: openHighSev.length,
            description: 'These serious incidents have been logged but no corrective action has been recorded. Requires immediate attention.',
            items: openHighSev.map(i => `${i.registration_no} (${i.severity})`),
            columns: ['Reg. No.', 'Incident Type', 'Severity', 'Date', 'Days Open', 'Facility'],
            rows: openHighSev.sort((a,b) => moment(a.incident_date).diff(moment(b.incident_date))).map(i => [
                <strong style={{ color: '#cf1322' }}>{i.registration_no}</strong>,
                i.incident_type,
                <Tag color={i.severity==='Critical'?'purple':'red'}>{i.severity}</Tag>,
                fmtD(i.incident_date),
                `${moment().diff(moment(i.incident_date),'days')} days`,
                i.facility,
            ]),
        });

        // ── 14. Overdue deployments ───────────────────────────────────────────
        const overdueDeps = deps.filter(d => {
            if (d.status === 'Returned') return false;
            const dl = d.expected_return && d.expected_return !== '0000-00-00'
                ? moment(d.expected_return).diff(moment(), 'days') : null;
            return dl !== null && dl < 0;
        });
        if (overdueDeps.length > 0) all.push({
            key: 'overdue_deps', priority: 'critical',
            icon: <GlobalOutlined />,
            title: 'Deployments past return date',
            count: overdueDeps.length,
            description: 'These vehicle deployments have passed their expected return date. Contact deployment leads for updates.',
            items: overdueDeps.map(d => `${d.event_name} (${Math.abs(moment(d.expected_return).diff(moment(),'days'))}d)`),
            columns: ['Event', 'Type', 'Expected Return', 'Days Overdue', 'Vehicles'],
            rows: overdueDeps.map(d => {
                const overDays = Math.abs(moment(d.expected_return).diff(moment(), 'days'));
                return [
                    <strong>{d.event_name}</strong>, d.deployment_type, fmtD(d.expected_return),
                    <Tag color="error">{overDays} days</Tag>,
                    <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                        {(d.vehicles||[]).filter(v=>!v.date_returned).slice(0,4).map(v=>(
                            <Tag key={v.vehicle_id} style={{ fontSize:10 }}>{v.registration_no}</Tag>
                        ))}
                    </div>,
                ];
            }),
        });

        // ── 15. Assigned driver — zero trip logs ──────────────────────────────
        const neverLogged = noTripData.filter(v => toN(v.trip_count) === 0);
        if (neverLogged.length > 0) all.push({
            key: 'never_logged_trips', priority: 'high',
            icon: <CarOutlined />,
            title: 'Assigned vehicles — no trip logs ever recorded',
            count: neverLogged.length,
            description: 'These vehicles have an active Primary driver but have never had a trip log submitted. This is an accountability gap — no movement history exists.',
            items: neverLogged.map(v => `${v.registration_no} (${v.driver_name})`),
            columns: ['Reg. No.', 'Driver', 'Assigned Since', 'Days Since Assign', 'Region', 'Facility'],
            rows: neverLogged.sort((a,b) => toN(b.days_since_trip)-toN(a.days_since_trip)).map(v => [
                <strong style={{ color:'#d46b08' }}>{v.registration_no}</strong>,
                <span style={{ fontWeight:500 }}>{v.driver_name}</span>,
                v.assigned_from ? moment(v.assigned_from).format('DD MMM YYYY') : '—',
                <Tag color="warning">{toN(v.days_since_trip)} days</Tag>,
                v.region || <span style={{ color:'#aaa' }}>No region</span>,
                v.facility,
            ]),
        });

        // ── 16. Assigned driver — last trip > 30 days ago ────────────────────
        const staleTrips = noTripData.filter(v => toN(v.trip_count) > 0 && toN(v.days_since_trip) > 30);
        if (staleTrips.length > 0) all.push({
            key: 'stale_trip_logs', priority: 'medium',
            icon: <ClockCircleOutlined />,
            title: 'Assigned vehicles — no trip logged in 30+ days',
            count: staleTrips.length,
            description: 'These vehicles have a driver assigned but no recent trip log. Either the vehicle is idle (utilisation concern) or the driver has stopped logging trips (compliance gap).',
            items: staleTrips.map(v => `${v.registration_no} (${toN(v.days_since_trip)}d ago)`),
            columns: ['Reg. No.', 'Driver', 'Last Trip', 'Days Silent', 'Total Trips', 'Facility'],
            rows: staleTrips.sort((a,b) => toN(b.days_since_trip)-toN(a.days_since_trip)).map(v => [
                v.registration_no, v.driver_name,
                v.last_trip_date ? moment(v.last_trip_date).format('DD MMM YYYY') : '—',
                <Tag color={toN(v.days_since_trip) > 90 ? 'error' : 'warning'}>{toN(v.days_since_trip)} days</Tag>,
                <span style={{ fontWeight:600 }}>{v.trip_count}</span>,
                v.facility,
            ]),
        });

        // ══════════════════════════════════════════════════════════════════════
        // DATA QUALITY ALERTS
        // ══════════════════════════════════════════════════════════════════════

        // ── 17. No region tagged ──────────────────────────────────────────────
        const noRegion = vehicles.filter(v => !v.region || v.region.trim() === '' || v.region.trim() === 'Unknown');
        if (noRegion.length > 0) all.push({
            key: 'no_region', priority: 'high',
            icon: <GlobalOutlined />,
            title: 'Vehicles with no region tagged',
            count: noRegion.length,
            description: 'These vehicles cannot appear in regional reports or be assigned to a regional admin. Tag a region (Hhohho, Manzini, Shiselweni, Lubombo) to enable regional tracking.',
            items: noRegion.slice(0,15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Driver', 'Facility'],
            rows: noRegion.slice(0,30).map(v => [
                <strong style={{ color:'#d46b08' }}>{v.registration_no}</strong>,
                v.vehicle_type || '—',
                v.current_driver_name || <span style={{ color:'#aaa' }}>No driver</span>,
                v.facility,
            ]),
        });

        // ── 18. No programme (data quality) ──────────────────────────────────
        const noProgVehicles = vehicles.filter(v => !v.assigned_programme || v.assigned_programme.trim() === '');
        if (noProgVehicles.length > 0) all.push({
            key: 'no_programme_dq', priority: 'high',
            icon: <FileTextOutlined />,
            title: 'Vehicles with no programme — blocks programme reporting',
            count: noProgVehicles.length,
            description: 'No programme is set. These vehicles will not appear in programme-level reports, cost allocations, or programme-scoped user views.',
            items: noProgVehicles.slice(0,15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Region', 'Facility'],
            rows: noProgVehicles.slice(0,30).map(v => [
                <strong style={{ color:'#d46b08' }}>{v.registration_no}</strong>,
                v.vehicle_type || '—',
                v.region || <span style={{ color:'#aaa' }}>No region</span>,
                v.facility,
            ]),
        });

        // ── 19. No functional status ──────────────────────────────────────────
        const noStatus = vehicles.filter(v => !v.functional_status || v.functional_status.trim() === '');
        if (noStatus.length > 0) all.push({
            key: 'no_functional_status', priority: 'high',
            icon: <ExclamationCircleOutlined />,
            title: 'Vehicles with no functional status',
            count: noStatus.length,
            description: 'Functional status (Serviceable/Unserviceable/Boarded) is required for fleet utilisation and availability KPIs.',
            items: noStatus.slice(0,15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Region', 'Facility'],
            rows: noStatus.slice(0,30).map(v => [v.registration_no, v.vehicle_type || '—', v.region || '—', v.facility]),
        });

        // ── 20. No fuel type ──────────────────────────────────────────────────
        const noFuelType = vehicles.filter(v => !v.fuel_type || v.fuel_type.trim() === '');
        if (noFuelType.length > 0) all.push({
            key: 'no_fuel_type', priority: 'medium',
            icon: <FireOutlined />,
            title: 'Vehicles with no fuel type set',
            count: noFuelType.length,
            description: 'Fuel type is required for service interval calculations, fuel consumption reports, and cost-per-km analysis.',
            items: noFuelType.slice(0,15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Region', 'Facility'],
            rows: noFuelType.slice(0,30).map(v => [v.registration_no, v.vehicle_type || '—', v.region || '—', v.facility]),
        });

        // ── 21. No odometer reading ───────────────────────────────────────────
        const noOdometer = vehicles.filter(v => !v.current_odometer || toN(v.current_odometer) === 0);
        if (noOdometer.length > 0) all.push({
            key: 'no_odometer', priority: 'medium',
            icon: <DashboardOutlined />,
            title: 'Vehicles with no odometer reading',
            count: noOdometer.length,
            description: 'Odometer is required for service scheduling, mileage tracking, cost-per-km, and fleet utilisation rate. Update during next driver assignment or inspection.',
            items: noOdometer.slice(0,15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Last Service', 'Facility'],
            rows: noOdometer.slice(0,30).map(v => [
                v.registration_no, v.vehicle_type || '—',
                v.last_service_date ? moment(v.last_service_date).format('DD MMM YYYY') : '—',
                v.facility,
            ]),
        });

        // ── 22. No procurement date ───────────────────────────────────────────
        const noProcDate = vehicles.filter(v => !v.procurement_date || v.procurement_date === '0000-00-00');
        if (noProcDate.length > 0) all.push({
            key: 'no_procurement_date', priority: 'medium',
            icon: <ClockCircleOutlined />,
            title: 'Vehicles with no procurement date',
            count: noProcDate.length,
            description: 'Procurement date is required for fleet ageing analysis, 15-year lifespan tracking, and disposal planning.',
            items: noProcDate.slice(0,15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Region', 'Facility'],
            rows: noProcDate.slice(0,30).map(v => [v.registration_no, v.vehicle_type || '—', v.region || '—', v.facility]),
        });

        // ── 23. No condition rating ───────────────────────────────────────────
        const noRating = vehicles.filter(v => !v.condition_rating || toN(v.condition_rating) === 0);
        if (noRating.length > 0) all.push({
            key: 'no_condition_rating', priority: 'medium',
            icon: <StarOutlined />,
            title: 'Vehicles with no condition rating',
            count: noRating.length,
            description: 'Condition rating (1–5) is required for maintenance prioritisation, disposal decisions, and fleet health KPIs.',
            items: noRating.slice(0,15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Type', 'Region', 'Facility'],
            rows: noRating.slice(0,30).map(v => [v.registration_no, v.vehicle_type || '—', v.region || '—', v.facility]),
        });

        // ── 24. Missing manufacturer / vehicle type ───────────────────────────
        const noManufacturer = vehicles.filter(v => !v.manufacturer || !v.vehicle_type);
        if (noManufacturer.length > 0) all.push({
            key: 'no_manufacturer', priority: 'low',
            icon: <CarOutlined />,
            title: 'Vehicles missing manufacturer or type',
            count: noManufacturer.length,
            description: 'Manufacturer and vehicle type are needed for fleet composition reports, spare parts tracking, and procurement planning.',
            items: noManufacturer.slice(0,15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Vehicle Type', 'Manufacturer', 'Facility'],
            rows: noManufacturer.slice(0,30).map(v => [
                v.registration_no,
                v.vehicle_type   || <span style={{ color:'#cf1322' }}>Missing</span>,
                v.manufacturer   || <span style={{ color:'#cf1322' }}>Missing</span>,
                v.facility,
            ]),
        });

        // ── 25. Data quality score summary ────────────────────────────────────
        const dqFields = ['region','assigned_programme','functional_status','fuel_type','current_odometer','condition_rating'];
        const dqScores = vehicles.map(v => {
            const filled = dqFields.filter(f => v[f] && String(v[f]).trim() !== '' && toN(v[f] || 1) !== 0).length;
            return filled / dqFields.length;
        });
        const avgDQ = dqScores.length
            ? Math.round(dqScores.reduce((s,n) => s+n, 0) / dqScores.length * 100)
            : 100;
        const poorDQ = vehicles.filter((v, i) => dqScores[i] < 0.5);
        if (poorDQ.length > 0 && avgDQ < 80) all.push({
            key: 'data_quality_score', priority: 'high',
            icon: <ThunderboltOutlined />,
            title: `Data completeness: ${avgDQ}% — ${poorDQ.length} vehicles below 50%`,
            count: poorDQ.length,
            description: `Fleet data is ${avgDQ}% complete across 6 key tracking fields (region, programme, status, fuel type, odometer, condition). Poor completeness affects all KPIs and reports.`,
            items: poorDQ.slice(0,15).map(v => v.registration_no),
            columns: ['Reg. No.', 'Region', 'Programme', 'Status', 'Fuel', 'Odometer', 'Rating'],
            rows: poorDQ.slice(0,20).map(v => {
                const ok  = (val) => val && String(val).trim() && toN(val||1) !== 0;
                const dot = (val) => ok(val)
                    ? <span style={{ color:'#52c41a' }}>✓</span>
                    : <span style={{ color:'#cf1322' }}>✗</span>;
                return [
                    <strong>{v.registration_no}</strong>,
                    dot(v.region), dot(v.assigned_programme), dot(v.functional_status),
                    dot(v.fuel_type), dot(v.current_odometer), dot(v.condition_rating),
                ];
            }),
        });

        return all.sort((a, b) => (PRIORITY[a.priority]?.order ?? 99) - (PRIORITY[b.priority]?.order ?? 99));
    }, [vehicles, drivers, incidents, costs, docs, deps, fuelData, fuelBalance, tcoData, noTripData]);

    const visible   = alerts;
    const critCount = visible.filter(a => a.priority === 'critical').length;
    const highCount = visible.filter(a => a.priority === 'high').length;

    if (loading) return (
        <div style={{ textAlign: 'center', padding: 80 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#888', fontSize: 13 }}>
                Running fleet health checks across all modules…
            </div>
        </div>
    );

    return (
        <div className="page-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <BellOutlined style={{ fontSize: 22, color: critCount > 0 ? '#cf1322' : '#d46b08' }} />
                        <h4 style={{ margin: 0, fontWeight: 700 }}>Fleet Notice Board</h4>
                        {critCount > 0 && (
                            <span style={{ background: '#cf1322', color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                                {critCount} critical
                            </span>
                        )}
                        {highCount > 0 && (
                            <span style={{ background: '#fa8c16', color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                                {highCount} high
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        {lastRefresh && `Last checked: ${moment(lastRefresh).format('DD MMM YYYY, HH:mm')} · `}
                        {visible.length} alert{visible.length !== 1 ? 's' : ''} across {alerts.length} categories
                        {(() => {
                            const r = getUserRole();
                            if (r.isAdmin || r.isNational) return ' · Scope: All regions';
                            if (r.isRegional) return ` · Scope: ${r.region} region`;
                            if (r.isClientAdmin) return ` · Scope: ${r.facility || 'Your facility'}`;
                            return '';
                        })()}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Summary strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
                {Object.entries(PRIORITY).map(([key, p]) => {
                    const count = visible.filter(a => a.priority === key).length;
                    return (
                        <div key={key} style={{
                            background: count > 0 ? p.bg : 'var(--color-background-secondary)',
                            border: `0.5px solid ${count > 0 ? p.border : 'var(--color-border-tertiary)'}`,
                            borderRadius: 8, padding: '10px 14px',
                            borderTop: `3px solid ${count > 0 ? p.dot : '#e8e8e8'}`,
                        }}>
                            <div style={{ fontSize: 10, color: count > 0 ? p.text : '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{p.label}</div>
                            <div style={{ fontSize: 28, fontWeight: 700, color: count > 0 ? p.dot : '#d9d9d9', lineHeight: 1.2, marginTop: 2 }}>{count}</div>
                            <div style={{ fontSize: 11, color: count > 0 ? p.text : '#aaa' }}>{count === 0 ? 'All clear' : `alert${count !== 1 ? 's' : ''}`}</div>
                        </div>
                    );
                })}
            </div>

            {/* Scope context badge */}
            {(() => {
                const r = getUserRole();
                if (!r.isNational && !r.isAdmin && !r.isRegional && !r.isClientAdmin && !r.isProgramme) return null;
                const scopeText = (r.isAdmin || r.isNational)
                    ? { label: 'National View', desc: 'Showing all vehicles across all regions and facilities', color: '#1a3a5c', bg: '#e6f4ff', border: '#91caff', icon: '🌐' }
                    : r.isRegional
                        ? { label: `${r.region} Region View`, desc: `Showing vehicles in the ${r.region} region only`, color: '#722ed1', bg: '#f9f0ff', border: '#d3adf7', icon: '🗺' }
                    : r.isProgramme
                        ? { label: `${r.programme} Programme View`, desc: `Showing vehicles assigned to the ${r.programme} programme across all facilities`, color: '#d46b08', bg: '#fff7e6', border: '#ffd591', icon: '📋' }
                        : { label: 'Facility View', desc: `Showing vehicles at ${r.facility || 'your facility'} only`, color: '#389e0d', bg: '#f6ffed', border: '#b7eb8f', icon: '🏥' };
                return (
                    <div style={{
                        background: scopeText.bg, border: `1px solid ${scopeText.border}`,
                        borderRadius: 8, padding: '8px 14px', marginBottom: 16,
                        display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
                    }}>
                        <span style={{ fontSize: 16 }}>{scopeText.icon}</span>
                        <span style={{ fontWeight: 600, color: scopeText.color }}>{scopeText.label}</span>
                        <span style={{ color: '#666' }}>— {scopeText.desc}</span>
                    </div>
                );
            })()}

            {/* All clear */}
            {visible.length === 0 && (
                <div style={{
                    textAlign: 'center', padding: '60px 40px',
                    background: '#f6ffed', border: '2px dashed #b7eb8f', borderRadius: 12,
                }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#389e0d', marginBottom: 6 }}>
                        Fleet is healthy — all checks passed
                    </div>
                    <div style={{ fontSize: 13, color: '#888' }}>
                        {alerts.length} checks ran. Last refreshed {lastRefresh ? moment(lastRefresh).format('HH:mm DD MMM') : 'just now'}. Data refreshes on every page load.
                    </div>
                </div>
            )}

            {/* Grouped alerts by priority */}
            {['critical','high','medium','info'].map(priority => {
                const group = visible.filter(a => a.priority === priority);
                if (!group.length) return null;
                const p = PRIORITY[priority];
                return (
                    <div key={priority} style={{ marginBottom: 24 }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            marginBottom: 12, paddingBottom: 8,
                            borderBottom: `2px solid ${p.dot}`,
                        }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.dot, display: 'inline-block' }} />
                            <span style={{ fontWeight: 600, fontSize: 12, color: p.text, textTransform: 'uppercase', letterSpacing: '.05em' }}>{p.label}</span>
                            <span style={{ fontSize: 11, color: '#aaa' }}>— {group.length} alert{group.length !== 1 ? 's' : ''}</span>
                        </div>
                        {group.map(alert => (
                            <AlertCard key={alert.key} alert={alert} onView={setDetailAlert} />
                        ))}
                    </div>
                );
            })}

            <AlertDetailModal alert={detailAlert} onClose={() => setDetailAlert(null)} />
        </div>
    );
}

// ─── Login Modal Briefing ─────────────────────────────────────────────────────
export function LoginBriefingModal({ vehicles = [], drivers = [], incidents = [], docs = [], deps = [], fuelBalance = [] }) {
    const [open, setOpen] = useState(true);

    const urgentItems = useMemo(() => {
        const items = [];
        const expDocs   = docs.filter(d => d.status === 'Expired').length;
        const licExp    = drivers.filter(d => toN(d.licence_days_remaining) < 0).length;
        const svcOvd    = vehicles.filter(v => v.service_due_flag === 'overdue').length;
        const poorCond  = vehicles.filter(v => v.condition_rating && toN(v.condition_rating) < 3).length;
        const highInc   = incidents.filter(i => i.status==='Open' && ['High','Critical'].includes(i.severity) && !i.action_taken).length;
        const negFuel   = fuelBalance.filter(v => toN(v.current_balance) < 0).length;
        const overdDeps = deps.filter(d => {
            if (d.status==='Returned') return false;
            const dl = d.expected_return ? moment(d.expected_return).diff(moment(),'days') : null;
            return dl !== null && dl < 0;
        }).length;

        if (expDocs   > 0) items.push({ icon:'🔴', text:`${expDocs} expired vehicle documents`,           priority:'critical' });
        if (licExp    > 0) items.push({ icon:'🔴', text:`${licExp} driver licences expired`,               priority:'critical' });
        if (poorCond  > 0) items.push({ icon:'🔴', text:`${poorCond} vehicles with rating < 3`,            priority:'critical' });
        if (highInc   > 0) items.push({ icon:'🔴', text:`${highInc} unactioned critical incidents`,        priority:'critical' });
        if (negFuel   > 0) items.push({ icon:'🔴', text:`${negFuel} vehicles with negative fuel balance`,  priority:'critical' });
        if (svcOvd    > 0) items.push({ icon:'🟠', text:`${svcOvd} vehicles overdue for service`,          priority:'high' });
        if (overdDeps > 0) items.push({ icon:'🟠', text:`${overdDeps} deployments past return`,            priority:'high' });

        return items;
    }, [vehicles, drivers, incidents, docs, deps, fuelBalance]);

    if (!open || urgentItems.length === 0) return null;

    return (
        <Modal open={open} onCancel={() => setOpen(false)} width={480}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <BellOutlined style={{ color: '#cf1322' }} />
                    <span>Fleet Briefing — {moment().format('DD MMM YYYY')}</span>
                </div>
            }
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={() => setOpen(false)}>Dismiss</Button>
                    <Button type="primary" style={{ background: '#1a3a5c' }}
                        onClick={() => { setOpen(false); window.location.hash = '#/fleet/notice-board'; }}>
                        View Notice Board →
                    </Button>
                </div>
            }>
            <div style={{ marginBottom: 12, fontSize: 13, color: '#888' }}>
                {urgentItems.length} item{urgentItems.length !== 1 ? 's' : ''} requiring your attention today:
            </div>
            {urgentItems.map((item, i) => (
                <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', marginBottom: 6,
                    background: item.priority === 'critical' ? '#fff1f0' : '#fff7e6',
                    border: `1px solid ${item.priority === 'critical' ? '#ffa39e' : '#ffd591'}`,
                    borderRadius: 6, fontSize: 13, fontWeight: 500,
                    color: item.priority === 'critical' ? '#cf1322' : '#d46b08',
                }}>
                    <span style={{ fontSize: 16 }}>{item.icon}</span>
                    {item.text}
                </div>
            ))}
            <div style={{ marginTop: 12, fontSize: 11, color: '#aaa', textAlign: 'center' }}>
                This briefing shows only today's critical and high-priority alerts.
            </div>
        </Modal>
    );
}